/**
 * MediaStrip — compacte horizontale foto/video-strip uit de eigen media-laag
 * (fase 2, CompanyCam-exit). Leest het bestaande GET /api/media?wiSfId=…
 * (staff-PIN-auth, zelfde headers als de upload-queue).
 *
 * Refresh via `refreshKey`: de pagina hoogt die op na het sluiten van de
 * CameraCapture. Omdat uploads via de offline-queue lopen kan een verse foto
 * enkele seconden achterlopen — daarom volgt na elke refresh één nalees-poging.
 *
 * Sterretje (Instagram-systeem, plan 2026-09-01): per thumbnail in een
 * wasserij-fase een toggle "mooi voor social". Optimistisch, PATCH
 * /api/media/kandidaat, terugdraaien bij fout. Bewust geen offline-wachtrij:
 * het signaal kan later ook nog gezet worden.
 */
import { useEffect, useState } from 'react';
import { useAuthStore } from '../stores';
import { MediaLightbox, type LightboxFoto } from './MediaLightbox';

interface MediaRij {
  id: string;
  url: string;
  type: 'foto' | 'video';
  fase: string;
  taken_at: string;
  social_kandidaat?: boolean;
}

// Klant-zichtbare fasen — MOET gelijk blijven aan KLANT_FASEN in de
// client-app-timeline en de office-MediaGrid: de fotonummers ("zie foto 3")
// worden over precies deze set berekend en moeten op alle schermen kloppen.
const KLANT_FASEN = ['voorinspectie', 'reinigen', 'na-inspectie'];

// Fasen waarin het sterretje zin heeft: wasserij-beeld van het kleed zelf.
// Nooit ophaal/aflevering (klantwoning), nooit klacht/risico.
const STER_FASEN = ['voorinspectie', 'reinigen', 'na-inspectie', 'social'];

/** Fotonummer per media-id: chronologisch, alleen foto's in klant-fasen. */
function klantFotoNummers(media: MediaRij[]): Map<string, number> {
  const nummers = new Map<string, number>();
  media
    .filter((m) => m.type === 'foto' && KLANT_FASEN.includes(m.fase))
    .sort((a, b) => a.taken_at.localeCompare(b.taken_at))
    .forEach((m, i) => nummers.set(m.id, i + 1));
  return nummers;
}

interface Props {
  wiSfId: string;
  /** Ophogen om opnieuw te laden (bv. na een capture-sessie). */
  refreshKey?: number;
  /**
   * Alleen deze fasen tonen (bv. ['reinigen'] op de Foto Tijdens-lijst).
   * Puur een weergavefilter: de klant-fotonummers worden over de volledige
   * set berekend, zodat "foto 3" hier hetzelfde is als in de klantlink.
   */
  toonFasen?: string[];
  /**
   * Meldt na het laden hoeveel items de strip (binnen toonFasen) toont —
   * zodat de pagina een vangnet kan tonen als er niets is (bv. de oude
   * CompanyCam-link voor kleden zonder eigen media).
   */
  onGeladen?: (aantal: number) => void;
}

const NALEES_MS = 4000;

export function MediaStrip({ wiSfId, refreshKey = 0, toonFasen, onGeladen }: Props) {
  const [media, setMedia] = useState<MediaRij[] | null>(null);
  const [lightboxStart, setLightboxStart] = useState<number | null>(null);
  const [sterFout, setSterFout] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function laad() {
      const { staff, pin } = useAuthStore.getState();
      if (!staff?.id || !pin) return;
      try {
        const res = await fetch(`/api/media?wiSfId=${encodeURIComponent(wiSfId)}`, {
          headers: { 'X-Staff-Id': staff.id, 'X-Staff-Pin': pin },
        });
        if (!res.ok) return;
        const data = (await res.json()) as { success: boolean; media: MediaRij[] };
        // Oplopend op taken_at, net als timeline en office-grid — zo lopen de
        // fotonummers gewoon van links naar rechts.
        if (!cancelled && data.success) {
          const rijen = [...data.media].sort((a, b) => a.taken_at.localeCompare(b.taken_at));
          setMedia(rijen);
          onGeladen?.(
            toonFasen ? rijen.filter((m) => toonFasen.includes(m.fase)).length : rijen.length
          );
        }
      } catch {
        // stil falen — de strip is puur informatief
      }
    }

    void laad();
    // Nalees: de upload-queue kan nog bezig zijn direct na een capture-sessie.
    timer = setTimeout(() => void laad(), NALEES_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [wiSfId, refreshKey]);

  async function toggleSter(m: MediaRij) {
    const { staff, pin } = useAuthStore.getState();
    if (!staff?.id || !pin) return;
    const nieuw = !m.social_kandidaat;
    const zet = (waarde: boolean) =>
      setMedia((prev) =>
        prev ? prev.map((r) => (r.id === m.id ? { ...r, social_kandidaat: waarde } : r)) : prev
      );
    zet(nieuw);
    setSterFout(null);
    try {
      const res = await fetch('/api/media/kandidaat', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Staff-Id': staff.id,
          'X-Staff-Pin': pin,
        },
        body: JSON.stringify({ id: m.id, socialKandidaat: nieuw }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch {
      zet(!nieuw);
      setSterFout(
        navigator.onLine
          ? 'Markeren lukt nu niet, probeer straks opnieuw.'
          : 'Markeren lukt niet offline, probeer straks.'
      );
    }
  }

  if (!media || media.length === 0) return null;

  // Nummeren vóór het filteren — zie de toelichting bij `toonFasen`.
  const nummers = klantFotoNummers(media);
  const zichtbaar = toonFasen ? media.filter((m) => toonFasen.includes(m.fase)) : media;
  if (zichtbaar.length === 0) return null;

  const fotos: LightboxFoto[] = zichtbaar
    .filter((m) => m.type === 'foto')
    .map((m) => ({
      id: m.id,
      url: m.url,
      label: nummers.has(m.id) ? `Foto ${nummers.get(m.id)}` : m.fase,
    }));
  const fotoIndex = new Map(fotos.map((f, i) => [f.id, i]));

  const sterKnop = (m: MediaRij) =>
    STER_FASEN.includes(m.fase) ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void toggleSter(m);
        }}
        aria-label={m.social_kandidaat ? 'Niet meer voor social' : 'Mooi voor social'}
        aria-pressed={m.social_kandidaat === true}
        className={`wb-focus absolute -top-1 -right-1 z-10 w-8 h-8 rounded-full border-wb flex items-center justify-center ${
          m.social_kandidaat ? 'bg-wb-ophalen border-wb-rand text-wb-inkt' : 'bg-wb-vlak border-wb-inkt3 text-wb-inkt3'
        }`}
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill={m.social_kandidaat ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.5a.56.56 0 011.04 0l2.13 5.11 5.52.44c.5.04.7.66.32.99l-4.2 3.6 1.28 5.38a.56.56 0 01-.84.61L12 16.73l-4.73 2.9a.56.56 0 01-.84-.61l1.28-5.38-4.2-3.6a.56.56 0 01.32-.99l5.52-.44 2.13-5.11z" />
        </svg>
      </button>
    ) : null;

  return (
    <div className="space-y-1">
      <div className="flex gap-2 overflow-x-auto pb-1 pt-1 pr-1">
        {zichtbaar.map((m) =>
          m.type === 'video' ? (
            <div key={m.id} className="relative flex-shrink-0">
              <video
                src={m.url}
                controls
                preload="metadata"
                playsInline
                className="h-20 w-20 rounded-wb object-cover bg-wb-gedempt border-wb border-wb-rand"
              />
              {sterKnop(m)}
            </div>
          ) : (
            <div key={m.id} className="relative flex-shrink-0">
              <button
                type="button"
                onClick={() => setLightboxStart(fotoIndex.get(m.id) ?? 0)}
                className="wb-focus relative block rounded-wb"
              >
                {nummers.has(m.id) && (
                  <span className="absolute top-0.5 left-0.5 z-10 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-[4px] bg-wb-inkt font-mono text-[11px] font-semibold text-white pointer-events-none">
                    {nummers.get(m.id)}
                  </span>
                )}
                <img
                  src={m.url}
                  alt={nummers.has(m.id) ? `Foto ${nummers.get(m.id)}` : ''}
                  loading="lazy"
                  className="h-20 w-20 rounded-wb object-cover bg-wb-gedempt border-wb border-wb-rand"
                />
              </button>
              {sterKnop(m)}
            </div>
          )
        )}
      </div>
      {lightboxStart !== null && fotos.length > 0 && (
        <MediaLightbox fotos={fotos} start={lightboxStart} onClose={() => setLightboxStart(null)} />
      )}
      {sterFout && (
        <p className="text-sm font-semibold text-wb-geldink" role="status">
          {sterFout}
        </p>
      )}
      <p className="font-mono text-xs text-wb-inkt2">
        {zichtbaar.length} {zichtbaar.length === 1 ? 'foto/video' : "foto's/video's"}
      </p>
    </div>
  );
}
